import * as i0 from '@angular/core';
import { EventEmitter, Directive, Output, Input, forwardRef, Component, ViewEncapsulation, ChangeDetectionStrategy, ViewChild, ContentChild, NgModule } from '@angular/core';
import { MXInputBaseValueComponent, MXLookComponent, AXComponent, AXFocusableComponent, AXClearableComponent, AXValuableComponent, AXCommonModule } from '@acorex/components/common';
import * as i1 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import IMask from 'imask';
import { classes } from 'polytype';
import { AXButtonModule } from '@acorex/components/button';
import { AXDecoratorModule } from '@acorex/components/decorators';
import { CommonModule } from '@angular/common';

class AXMaskOptionsDirective {
    constructor() {
        this.onMaskChange = new EventEmitter();
    }
    get mask() {
        return this._mask;
    }
    set mask(v) {
        this._mask = v;
        this.onMaskChange.emit(v);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXMaskOptionsDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.2", type: AXMaskOptionsDirective, selector: "ax-mask-options", inputs: { mask: "mask", updateValue: "updateValue" }, outputs: { onMaskChange: "onMaskChange" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXMaskOptionsDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ax-mask-options',
                }]
        }], propDecorators: { onMaskChange: [{
                type: Output
            }], mask: [{
                type: Input
            }], updateValue: [{
                type: Input
            }] } });

/* eslint-disable @angular-eslint/no-inputs-metadata-property */
/**
 * The Textbox is a component which detects user interaction and triggers a corresponding event
 *
 * @category Components
 */
class AXTextBoxComponent extends classes((MXInputBaseValueComponent), MXLookComponent) {
    constructor() {
        super(...arguments);
        this._updateOn = 'change';
        this._maskOptions = { mask: null };
    }
    get maskOptions() {
        return this._maskOptions;
    }
    set maskOptions(value) {
        this._maskOptions = value;
        this.setMask();
    }
    ngAfterContentInit() {
        if (this._maskOptionsContent?.mask) {
            if (typeof this._maskOptionsContent.mask == 'string' || this._maskOptionsContent.mask instanceof RegExp) {
                Object.assign(this.maskOptions, {
                    mask: this._maskOptionsContent.mask,
                    unmask: false,
                });
            }
            if (typeof this._maskOptionsContent.mask == 'object') {
                Object.assign(this.maskOptions, this._maskOptionsContent.mask);
            }
        }
        //
        this.setMask();
    }
    setMask() {
        if (this._maskObj)
            this._maskObj.destroy();
        //
        if (this.maskOptions?.mask) {
            this._maskObj = IMask(this.input.nativeElement, this.maskOptions);
            this._updateOn = 'submit';
            this._maskObj.on('accept', this._onComplete.bind(this));
            this._maskObj.on('complete', this._onComplete.bind(this));
            if (this.value) {
                this._maskObj.value = `${this.value}`;
                this._maskObj.updateValue();
            }
        }
    }
    _onComplete(e) {
        this.commitValue(this._maskObj.value, e?.isTrusted ?? false);
        if (typeof this._maskOptionsContent?.updateValue === 'function') {
            this._maskOptionsContent?.updateValue(this._maskObj.value, this._maskObj.unmaskedValue);
        }
    }
    _handleModelChange(value) {
        this.commitValue(value, true);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTextBoxComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.0.2", type: AXTextBoxComponent, selector: "ax-text-box", inputs: { disabled: "disabled", tabIndex: "tabIndex", readonly: "readonly", value: "value", state: "state", name: "name", id: "id", placeholder: "placeholder", maxLength: "maxLength", allowNull: "allowNull", type: "type", autoComplete: "autoComplete", look: "look", maskOptions: ["mask-options", "maskOptions"], classNames: ["class", "classNames"] }, outputs: { onBlur: "onBlur", onFocus: "onFocus", valueChange: "valueChange", stateChange: "stateChange", onValueChanged: "onValueChanged", readonlyChange: "readonlyChange", disabledChange: "disabledChange", onKeyDown: "onKeyDown", onKeyUp: "onKeyUp", onKeyPress: "onKeyPress" }, providers: [
            { provide: AXComponent, useExisting: AXTextBoxComponent },
            { provide: AXFocusableComponent, useExisting: AXTextBoxComponent },
            { provide: AXClearableComponent, useExisting: AXTextBoxComponent },
            { provide: AXValuableComponent, useExisting: AXTextBoxComponent },
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AXTextBoxComponent),
                multi: true,
            },
        ], queries: [{ propertyName: "_maskOptionsContent", first: true, predicate: AXMaskOptionsDirective, descendants: true, static: true }], viewQueries: [{ propertyName: "input", first: true, predicate: ["input"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"ax-editor-container ax-look-{{look}} {{classNames}}\" [class.ax-state-disabled]=\"disabled\">\n  <ng-content select=\"ax-prefix\"> </ng-content>\n  <input #input class=\"ax-input\" [id]=\"id\" [name]=\"name\" [attr.autocomplete]=\"autoComplete\" [attr.type]=\"type\"\n    [attr.placeholder]=\"placeholder\" [attr.maxlength]=\"maxLength\" [class.ax-state-disabled]=\"disabled\"\n    [class.ax-state-readonly]=\"readonly\" [disabled]=\"disabled\" [readonly]=\"readonly\" [tabindex]=\"tabIndex\"\n    [ngModel]=\"value\" (ngModelChange)=\"_handleModelChange($event)\" [ngModelOptions]=\"{ updateOn: _updateOn }\"\n    (keydown)=\"emitOnKeydownEvent($event)\" (keyup)=\"emitOnKeyupEvent($event)\" (keypress)=\"emitOnKeypressEvent($event)\"\n    (focus)=\"emitOnFocusEvent($event)\" (blur)=\"emitOnBlurEvent($event)\" />\n  @if(input.value && !disabled && !readonly){\n  <ng-content select=\"ax-clear-button\"></ng-content>\n  }\n  <ng-content select=\"ax-suffix\"> </ng-content>\n</div>\n<ng-content select=\"ax-validation-rule\"> </ng-content>\n<div class=\"ax-error-container\"></div>", styles: ["ax-text-box{width:100%}\n"], dependencies: [{ kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTextBoxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ax-text-box', inputs: [
                        'disabled',
                        'tabIndex',
                        'readonly',
                        'value',
                        'state',
                        'name',
                        'id',
                        'placeholder',
                        'maxLength',
                        'allowNull',
                        'type',
                        'autoComplete',
                        'look',
                    ], outputs: [
                        'onBlur',
                        'onFocus',
                        'valueChange',
                        'stateChange',
                        'onValueChanged',
                        'readonlyChange',
                        'disabledChange',
                        'onKeyDown',
                        'onKeyUp',
                        'onKeyPress',
                    ], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        { provide: AXComponent, useExisting: AXTextBoxComponent },
                        { provide: AXFocusableComponent, useExisting: AXTextBoxComponent },
                        { provide: AXClearableComponent, useExisting: AXTextBoxComponent },
                        { provide: AXValuableComponent, useExisting: AXTextBoxComponent },
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AXTextBoxComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"ax-editor-container ax-look-{{look}} {{classNames}}\" [class.ax-state-disabled]=\"disabled\">\n  <ng-content select=\"ax-prefix\"> </ng-content>\n  <input #input class=\"ax-input\" [id]=\"id\" [name]=\"name\" [attr.autocomplete]=\"autoComplete\" [attr.type]=\"type\"\n    [attr.placeholder]=\"placeholder\" [attr.maxlength]=\"maxLength\" [class.ax-state-disabled]=\"disabled\"\n    [class.ax-state-readonly]=\"readonly\" [disabled]=\"disabled\" [readonly]=\"readonly\" [tabindex]=\"tabIndex\"\n    [ngModel]=\"value\" (ngModelChange)=\"_handleModelChange($event)\" [ngModelOptions]=\"{ updateOn: _updateOn }\"\n    (keydown)=\"emitOnKeydownEvent($event)\" (keyup)=\"emitOnKeyupEvent($event)\" (keypress)=\"emitOnKeypressEvent($event)\"\n    (focus)=\"emitOnFocusEvent($event)\" (blur)=\"emitOnBlurEvent($event)\" />\n  @if(input.value && !disabled && !readonly){\n  <ng-content select=\"ax-clear-button\"></ng-content>\n  }\n  <ng-content select=\"ax-suffix\"> </ng-content>\n</div>\n<ng-content select=\"ax-validation-rule\"> </ng-content>\n<div class=\"ax-error-container\"></div>", styles: ["ax-text-box{width:100%}\n"] }]
        }], propDecorators: { input: [{
                type: ViewChild,
                args: ['input', { static: true }]
            }], maskOptions: [{
                type: Input,
                args: ['mask-options']
            }], maxLength: [{
                type: Input
            }], type: [{
                type: Input
            }], autoComplete: [{
                type: Input
            }], classNames: [{
                type: Input,
                args: ['class']
            }], _maskOptionsContent: [{
                type: ContentChild,
                args: [AXMaskOptionsDirective, { static: true }]
            }] } });

class AXTextBoxModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTextBoxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.2", ngImport: i0, type: AXTextBoxModule, declarations: [AXTextBoxComponent, AXMaskOptionsDirective], imports: [CommonModule, AXCommonModule, FormsModule, AXButtonModule, AXDecoratorModule], exports: [AXTextBoxComponent, AXMaskOptionsDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTextBoxModule, imports: [CommonModule, AXCommonModule, FormsModule, AXButtonModule, AXDecoratorModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXTextBoxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, AXCommonModule, FormsModule, AXButtonModule, AXDecoratorModule],
                    exports: [AXTextBoxComponent, AXMaskOptionsDirective],
                    declarations: [AXTextBoxComponent, AXMaskOptionsDirective],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AXMaskOptionsDirective, AXTextBoxComponent, AXTextBoxModule };
